import React, { useEffect } from 'react';
import { LinearProgress, makeStyles } from '~/lib/bn-material-ui';
import { useFullSirenCollection, useSirenEntity, useStore } from '~/utility/BinSentry-ui-utility';
import { StateProvider, useStateValue } from './data/order-calculator-context';
import BinLevels from './bin-levels';
import { BnErrorBoundary } from '~/common/bn-error-boundary';
import Order from './order';
import OrderSuggestionSettingsBar from './order-suggestion-settings-bar';
import { DateTime } from 'luxon';
import useBinLevelBinProperties from '~/common/use-bin-level-bin-properties';

const useStyles = makeStyles((theme) => {
  return {
    container: {
      height: '100%',
      overflow: 'auto',
      paddingBottom: '3em',
    },
    calendarContainer: {
      padding: '0 1rem',
      backgroundColor: 'white',
      border: `1px solid ${theme.palette.gray[40]}`,
      borderWidth: '1px 0',
    },
    unavailableContainer: {
      padding: '2rem',
    },
  };
});

function OrderCalculatorContent({ binId }) {
  const styles = useStyles();

  const [{ activeOrderId }, dispatch] = useStateValue();

  const { entity: bin, loading: binLoading } = useSirenEntity({ href: binId });

  const statsHref = bin?.getSubEntityByRel('https://api.binsentry.com/rel/bin-stat-latest').href;
  const ordersHref = bin?.getSubEntityByRel('https://api.binsentry.com/rel/orders').href;

  const { entity: stats } = useSirenEntity({ href: statsHref }); // may be undefined on bins with less than 8 reading
  const { collection: orders, loading: ordersLoading } = useFullSirenCollection({ href: ordersHref, onlyLoadFirstPage: false });
  const { entity: activeOrderEntity } = useSirenEntity({ href: activeOrderId });

  useEffect(() => dispatch({ type: 'setOrdersHref', ordersHref }), [dispatch, ordersHref]);
  useEffect(() => dispatch({ type: 'setOrders', orders }), [dispatch, orders]);
  const {
    binLevelProperties,
    loading: binLevelPropertiesLoading,
  } = useBinLevelBinProperties({ binId });

  useEffect(() => {
    if (!binLevelPropertiesLoading) {
      dispatch({ type: 'setSmoothingModel', smoothingModel: binLevelProperties?.properties.smoothingModel });
    }
  }, [dispatch, binLevelProperties, binLevelPropertiesLoading]);

  useEffect(() => {
    activeOrderEntity && dispatch({
      type: 'setBinLevelTargetDate',
      binLevelTargetDate: DateTime.fromISO(activeOrderEntity.properties.deliveryDate),
    });
  }, [dispatch, activeOrderEntity]);

  if (binLoading || binLevelPropertiesLoading || ordersLoading) {
    return <LinearProgress />;
  }

  const binLevelsHref = bin?.getSubEntityByRel('https://api.binsentry.com/rel/bin-levels')?.href;

  if (!binLevelsHref) {
    return (
      <div className={styles.unavailableContainer}>
        Order calculator unavailable until at least one bin level reading exists for this bin.
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <OrderSuggestionSettingsBar binHref={binId} />

      <div className={styles.calendarContainer}>
        <BinLevels href={binLevelsHref} stats={stats} fullTotal={bin.properties.volumes.fullTotal} />
      </div>

      <Order bin={bin} stats={stats} />
    </div>
  );
}

export default function OrderCalculator({ binId, activeOrderId }) {
  const { isProducer } = useStore();

  const binLevelTargetDate = DateTime.now();
  const binLevelDayCount = 7;
  const start = binLevelTargetDate.minus({ days: 2 }).startOf('day');
  const end = start.plus({ days: binLevelDayCount });

  return (
    <BnErrorBoundary locationTag='order-calculator'>
      <StateProvider initialState={{
        activeOrderId,
        binId,
        binLevelDayCount,
        binLevelTargetDate,
        binLevelDateRange: { start, end },
        isProducer,
      }}>
        <OrderCalculatorContent binId={binId} />
      </StateProvider>
    </BnErrorBoundary>
  );
}
